// Hooks
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import useCountdownTimer from '../../../hooks/useCountdownTimer';
import useSessionAccess from '../../../hooks/useSessionAccess';

// Features
import { selectUser } from '../../../features/user/userSlice';
import { selectSessionStatus } from '../../../features/session/sessionSlice';

// Models
import { LobbySession as Session } from '../../../features/lobby/lobbyModels';

// Components
import Button from '../../atoms/Button/Button';
import Countdown from '../../atoms/Countdown/Countdown';

// Styles
import styles from './LobbySession.module.css';

/**
 * @function LobbySession
 * Describes an available session in the lobby
 *
 * @example
 * return (
 *   <LobbySession session={{}} inAnotherSession={false} />
 * )
 */
function LobbySession({
    session,
    inAnotherSession,
}: LobbySessionProps): JSX.Element {
    const user = useAppSelector(selectUser);
    const sessionIsToken = session.session.is_access_token;
    const daubing = session.session.is_manual_daubing ? 'Manual' : 'Auto';
    const game =
        session.status !== null
            ? session.status.gameNumber !== null
                ? session.status.gameNumber
                : 1
            : 1;
    const players =
        session.status !== null
            ? session.status.players !== null
                ? session.status.players
                : 0
            : 0;
    const startTime = new Date(`${session.start_at}`);
    const timeDiff = useCountdownTimer(startTime);
    const sessionAccess = useSessionAccess(
        session,
        session.id,
        `Play <b>${daubing}</b> Bingo`,
        inAnotherSession,
    );

    return (
        <article className={styles.container}>
            <header
                className={`${styles.header} ${
                    sessionIsToken && user.active_access_token_count > 0
                        ? styles['has-token']
                        : sessionIsToken && user.active_access_token_count <= 0
                        ? styles['no-token']
                        : ''
                }`}
            >
                <h2 className={styles.title}>{session.session.name}</h2>
            </header>
            <figure className={styles.figure}>
                <figcaption>{session.session.theme.name} Room</figcaption>
                <img
                    src={`https://${
                        process.env.REACT_APP_CDN
                    }/images/banners/lobby/lobby-room-${
                        session.session.theme.slug === 'manhattan'
                            ? `the-${session.session.theme.slug}`
                            : session.session.theme.slug
                    }.png`}
                    alt={session.session.theme.name}
                />
            </figure>
            {timeDiff < 1 ? (
                <div className={styles.progress}>
                    <h3 className={styles.status}>In progress</h3>
                    <div className={styles.spinner}>
                        {(session.session.totalGames === 5 ||
                            session.session.totalGames === 12) && (
                            <img
                                src={`https://${process.env.REACT_APP_CDN}/play-bingo/images/graphics/graphic-progress-${game}-${session.session.totalGames}.svg`}
                                alt={`Game ${game} of ${session.session.totalGames}`}
                            />
                        )}
                        <p style={{ textAlign: 'center' }}>
                            {session.session.totalGames > 1 ? (
                                <>
                                    Currently playing game {game} of{' '}
                                    {session.session.totalGames}
                                </>
                            ) : (
                                <>Currently playing</>
                            )}
                        </p>
                    </div>
                </div>
            ) : (
                <div className={styles.timer}>
                    <h3 className={styles.status}>Starting in</h3>
                    <Countdown time={timeDiff} />
                </div>
            )}
            <div className={styles.body}>
                <dl className={styles.stats}>
                    <dt>Fee</dt>
                    <dd>
                        {session.session.entryPrice > 0 &&
                            `${session.session.entryPrice.toLocaleString(
                                'en-US',
                            )} PP`}
                        {sessionIsToken && (
                            <>
                                {session.session.entryPrice > 0 && ' + '}1{' '}
                                <img
                                    className={styles.token}
                                    alt="Access token"
                                    src={`https://${process.env.REACT_APP_CDN}/play-bingo/images/icons/icon-access-token.svg`}
                                />
                            </>
                        )}
                    </dd>
                    <dt>Games</dt>
                    <dd>{session.session.totalGames}</dd>
                    <dt>Daubing</dt>
                    <dd>{daubing}</dd>
                    <dt>Total prizes</dt>
                    <dd>
                        Up to{' '}
                        {session.session.totalPoints.toLocaleString('en-US')} PP
                    </dd>
                    <dt>Players</dt>
                    <dd>{players}</dd>
                </dl>
                <Button
                    text={sessionAccess.text}
                    style="primary"
                    onClick={() => sessionAccess.onClick()}
                    disabled={sessionAccess.disabled}
                />
            </div>
        </article>
    );
}

/**
 * @interface LobbySessionProps
 * Describes the properties required for the LobbySession component
 *
 * @property {Session} session
 * @property {boolean} inAnotherSession
 */
export interface LobbySessionProps {
    session: Session;
    inAnotherSession: boolean;
}

// Exports
export default LobbySession;
